import { Box, COLOR_PALETTE, Ellipsis, Group, Icon, Stack, Text } from '@inflearn/ds-react';
import { faChevronRight, faFire, faSignalStream } from '@inflearn/pro-solid-svg-icons';
import type { UpcomingEventResponse } from '../../../../../../resources/@types/courseSchema';
import { INFLEARN_URL } from '../../../../../constants/urls';

const UNIT_TYPE_CONFIG = {
  LIVE_SESSION: {
    label: '라이브',
    bgColor: 'pink.6',
    icon: faSignalStream,
  },
  MISSION: {
    label: '미션',
    bgColor: 'cyan.6',
    icon: faFire,
  },
} as const;

type SupportedUnitType = keyof typeof UNIT_TYPE_CONFIG;

function isSupportedUnitType(unitType: string): unitType is SupportedUnitType {
  return unitType in UNIT_TYPE_CONFIG;
}

function formatEventDate(dateString?: string): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  const year = date.getFullYear().toString().slice(2);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${year}년 ${month}월 ${day}일 ${hours}:${minutes}`;
}

interface UpcomingEventCardProps {
  event: UpcomingEventResponse;
  onClick?: () => void;
}

export function UpcomingEventCard({ event, onClick }: UpcomingEventCardProps) {
  const { unitType, unitTitle, startedAtUTC, endedAtUTC } = event;

  if (!isSupportedUnitType(unitType)) {
    return null;
  }

  const config = UNIT_TYPE_CONFIG[unitType];

  return (
    <Box
      component="a"
      onClick={onClick}
      bg="white"
      h={58}
      w={300}
      px={12}
      py={10}
      style={{
        border: `1px solid ${COLOR_PALETTE.gray[2]}`,
        borderRadius: '12px',
        cursor: 'pointer',
        flexShrink: 0,
      }}
      href={INFLEARN_URL.COURSES_LECTURE({
        courseId: event.courseId,
        unitId: event.unitId,
      })}
    >
      <Group spacing="12px" noWrap h="100%">
        <Box
          bg={config.bgColor}
          w={32}
          h={32}
          style={{
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon icon={config.icon} size="md" color="white" />
        </Box>

        <Stack css={{ flex: 1 }}>
          <Group spacing="2px" noWrap>
            <Text fz={14} fw={600} c="gray.7">
              {config.label}
            </Text>
            <Text fz={14} fw={600} c="gray.5">
              ·
            </Text>
            <Text fz={14} fw={600} c="gray.7">
              {unitType === 'MISSION'
                ? formatEventDate(endedAtUTC) + ' 까지'
                : formatEventDate(startedAtUTC)}
            </Text>
          </Group>
          <Ellipsis lineClamp={1}>
            <Text fz={12} c="gray.5" align="left">
              {unitTitle}
            </Text>
          </Ellipsis>
        </Stack>

        {/* 화살표 아이콘 */}
        <Icon icon={faChevronRight} size="xs" color="gray.5" />
      </Group>
    </Box>
  );
}
